import React from 'react';
import { Check } from 'lucide-react';
import { LEGAL_FLOW_STAGES, legalStageIndex } from '../../../shared/utils/legalAccess';

/**
 * Where this deal sits in the post-sales flow.
 *
 * Worth drawing because the order is the thing people get wrong: legal
 * verification is what releases priorities and invoices, and Management
 * countersigning is not a step the deal waits on. A list of statuses does not
 * say that; a line with the stages on it does.
 */
const LegalFlowStrip = ({ status, compact = false, squared = false }) => {
  const reached = legalStageIndex(status);

  return (
    <ol className={`flex flex-wrap items-center ${compact ? 'gap-x-1.5 gap-y-2' : 'gap-x-2 gap-y-2'}`}>
      {LEGAL_FLOW_STAGES.map((stage, index) => {
        const done = index < reached;
        const current = index === reached;
        return (
          <li key={stage.id} className="flex items-center gap-1.5">
            <span
              className={[
                // Shape AND neutrals are scoped together. #CBD5E1 against the
                // old #E2E8F0 connector is a visible step, and the embedded
                // copies were not asked to move at all.
                //
                // Squared only where Legal owns the screen. This strip is also
                // embedded in the Sales deal hub and in Management task
                // details, and those screens were not asked to change — so the
                // round shape stays the default and Legal opts out of it.
                `inline-flex items-center gap-1.5 font-medium ${
                  squared ? 'rounded-control' : 'rounded-full'
                }`,
                compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
                done
                  ? 'bg-success-bg text-success-fg ring-1 ring-success-border'
                  : current
                    ? 'bg-brand-700 text-white'
                    : squared
                    ? 'bg-surface-muted text-gray-500 ring-1 ring-hairline'
                    : 'bg-slate-50 text-slate-500 ring-1 ring-slate-200'
              ].join(' ')}>
              {done ? <Check className="h-3 w-3" aria-hidden /> : null}
              {stage.label}
            </span>
            {index < LEGAL_FLOW_STAGES.length - 1 ? (
              <span
                aria-hidden
                className={`h-px w-4 ${
                  done ? 'bg-success-border' : squared ? 'bg-hairline-strong' : 'bg-slate-200'
                }`}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
};

export default LegalFlowStrip;
